import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useHubLang } from '../lib/i18n'
import { GALLERY_BROWSE, HOME } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import { signedPhotoUrl } from '../lib/photos'
import {
  BROWSE_PAGE,
  browseGallery,
  myPhotoReviewState,
  type BrowsePhoto,
  type BrowseSort,
  type MyReviewState,
} from '../lib/photoReview'
import { PhotoReviewButton } from './PhotoReviewButton'
import { HelpButton } from './HelpButton'
import { SearchIcon } from './icons'

/**
 * 152. The Gallery browser's view (photo.browse, or everybody while
 * photo_policy.browse_for_everyone is on): a scrolling feed of the Gallery's
 * photos at FEED size — the 2000px copy every post already has — newest or
 * oldest first, and a search over the captions. What the feed already shows
 * and nothing more: caption, who posted it, a link to the post. No download,
 * no tags, no place, and nothing recorded (rule 8: browsing is reading).
 *
 * Server-paged (rule 1): photo_gallery_browse applies the limit and counts the
 * whole match; "Show more" asks for the next page.
 */
export function GalleryBrowse() {
  const lang = useHubLang()
  const t = GALLERY_BROWSE[lang]
  const [photos, setPhotos] = useState<BrowsePhoto[] | null>(null)
  const [total, setTotal] = useState(0)
  const [states, setStates] = useState<Map<string, MyReviewState>>(new Map())
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<BrowseSort>('newest')
  const [error, setError] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  // One read of the review state per page of photos, not one per photo.
  const readStates = useCallback((paths: string[]) => {
    myPhotoReviewState(paths)
      .then((m) => setStates((cur) => new Map([...cur, ...m])))
      .catch(() => {})
  }, [])

  // Latest-wins, as in the room: a slow page must not land under a new search.
  const seq = useRef(0)
  useEffect(() => {
    const mine = ++seq.current
    setError(null)
    setPhotos(null)
    browseGallery({ query: search, sort })
      .then((page) => {
        if (mine !== seq.current) return
        setPhotos(page.photos)
        setTotal(page.total)
        readStates(page.photos.map((p) => p.path))
      })
      .catch((e) => mine === seq.current && setError(messageOf(e) || t.couldNotLoad))
  }, [search, sort, readStates, t.couldNotLoad])

  async function more() {
    if (!photos) return
    setLoadingMore(true)
    try {
      const next = await browseGallery({ query: search, sort }, photos.length, BROWSE_PAGE)
      const have = new Set(photos.map((p) => p.path))
      const fresh = next.photos.filter((p) => !have.has(p.path))
      setPhotos([...photos, ...fresh])
      setTotal(next.total)
      readStates(fresh.map((p) => p.path))
    } catch {
      setError(t.couldNotLoad)
    } finally {
      setLoadingMore(false)
    }
  }

  const dateOf = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric' })

  return (
    <div className="gallerybrowse">
      <form
        className="phototools"
        role="search"
        onSubmit={(e) => {
          e.preventDefault()
          setSearch(query.trim())
        }}
      >
        <div className="phototools__search">
          <span className="phototools__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <label className="sr-only" htmlFor="gallerybrowse-q">
            {t.searchLabel}
          </label>
          <input
            id="gallerybrowse-q"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            maxLength={200}
          />
          {search && (
            <button
              type="button"
              className="phototools__clear"
              aria-label={t.clearSearch}
              title={t.clearSearch}
              onClick={() => {
                setQuery('')
                setSearch('')
              }}
            >
              ×
            </button>
          )}
          <button type="submit" className="btn btn--primary btn--sm phototools__go" disabled={!query.trim()}>
            {t.search}
          </button>
        </div>
        <label className="phototools__field">
          <span className="sr-only">{t.sortLabel}</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as BrowseSort)} aria-label={t.sortLabel}>
            <option value="newest">{t.sort.newest}</option>
            <option value="oldest">{t.sort.oldest}</option>
          </select>
        </label>
        <HelpButton title={t.helpTitle} label={t.helpLabel} className="phototools__help">
          {t.help.map((para) => (
            <p key={para}>{para}</p>
          ))}
        </HelpButton>
      </form>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      {photos === null && !error && <p className="queue__empty">{t.loading}</p>}
      {photos !== null && photos.length === 0 && <p className="queue__empty">{search ? t.noMatches : t.empty}</p>}

      {photos !== null && photos.length > 0 && (
        <>
          <p className="photoroom__count">{t.shownOf(photos.length, total)}</p>
          <ul className="gallerybrowse__feed">
            {photos.map((p) => (
              <li key={p.path} className="gallerybrowse__item">
                <FeedSizePhoto path={p.path} width={p.width} height={p.height} />
                <div className="gallerybrowse__body">
                  {p.caption ? (
                    <p className="gallerybrowse__caption">{p.caption}</p>
                  ) : (
                    <p className="gallerybrowse__caption gallerybrowse__caption--none">{t.noCaption}</p>
                  )}
                  <p className="gallerybrowse__meta">
                    {p.bylineName ? `${p.bylineName} · ${HOME[lang].postedBy(p.uploaderName)}` : p.uploaderName} · {dateOf(p.postedAt)} ·{' '}
                    <Link to={`/item/${p.contentId}?photo=${encodeURIComponent(p.path)}`}>{t.openPost}</Link>
                  </p>
                  <PhotoReviewButton
                    path={p.path}
                    state={states.get(p.path) ?? null}
                    onChanged={() => readStates([p.path])}
                  />
                </div>
              </li>
            ))}
          </ul>
          {photos.length < total && (
            <button type="button" className="btn btn--quiet btn--sm photoroom__more" disabled={loadingMore} onClick={() => void more()}>
              {t.showMore}
            </button>
          )}
        </>
      )}
    </div>
  )
}

/** The feed copy, at the width of the column. No download control. */
function FeedSizePhoto({ path, width, height }: { path: string; width: number | null; height: number | null }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setUrl(null)
    signedPhotoUrl(path)
      .then((u) => live && setUrl(u))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [path])
  const ratio = width && height ? `${width} / ${height}` : '4 / 3'
  return url ? (
    <img className="gallerybrowse__img" src={url} alt="" loading="lazy" width={width ?? undefined} height={height ?? undefined} />
  ) : (
    <span className="gallerybrowse__img gallerybrowse__img--wait" style={{ aspectRatio: ratio }} aria-hidden="true" />
  )
}
